import React, { PureComponent, ReactElement } from "react";
import {
  zoomModeInfo,
  headerUnitInfo,
  VERTICAL_SEPARATOR_COLOR,
  HEADER_UNIT_SUB_MONTH,
  HEADER_UNIT_SUB_DAY,
  HEADER_UNIT_SUB_HOUR,
  SELECT_GREEN,
  HEADER_BACKGROUND,
  HEADER_BACKGROUND_WEEKEND,
  HEADER_BACKGROUND_TODAY,
  VIEWPORT_TITLE_HEIGHT,
  BORDER_COLOR,
  ZOOM_MODE_WEEK,
  ZOOM_MODE_MONTH,
  ZOOM_MODE_DAY,
  ZOOM_MODE_YEAR,
} from "../../helpers/Const";
import { differenceInHours, isWeekend, isToday, isThisMonth, isThisHour } from "date-fns";
import styled from "styled-components/macro";
import { getChartTotalWidth } from "../../helpers/StyleHelpers";
import { getNextRoundedDate, isHoliday, formatWithLocale as format } from "../../helpers/DateHelpers";
import { connect } from "react-redux";
import { withTranslation, WithTranslation } from "react-i18next";
import { DisplaySettings } from "src/rematch/interfaces/states/IViewSettingsState";
import { Dispatch, iRootState } from "src/rematch/store";

interface IDateElementProps {
  rowIndex: number;
  useTodayColour: boolean;
  isWeekend: boolean;
  width: number;
}

const DateElement = styled.div.attrs((props: IDateElementProps) => {
  let bgOverride: string;
  if (props.rowIndex === 1) {
    if (props.useTodayColour) {
      bgOverride = HEADER_BACKGROUND_TODAY;
    } else if (props.isWeekend) {
      bgOverride = HEADER_BACKGROUND_WEEKEND;
    }
  }
  return {
    style: {
      flexBasis: props.width,
      background: bgOverride,
    },
  };
})`
  border: ${(props: IDateElementProps) => (props.useTodayColour ? "2px solid " + SELECT_GREEN : "")};
  flex: 0 0 auto;
  overflow: hidden;
  background: ${HEADER_BACKGROUND};
`;

interface IDateElementInnerProps {
  justify: boolean;
  isClickable: boolean;
}

//These additional inner element rules could be merged with the DateElement rules, were it not for IE11.
const DateElementInner = styled.div`
  cursor: ${(props: IDateElementInnerProps) => (props.isClickable ? "pointer" : "default")};
  padding-left: ${(props: IDateElementInnerProps) => (props.justify ? "0" : "4px")};
  width: 100%;
  height: 100%;
  white-space: nowrap;
  display: flex;
  justify-content: ${(props: IDateElementInnerProps) => (props.justify ? "center" : "flex-start")};
  align-items: center;
`;

const HeaderRow = styled.div`
  display: flex;
  flex: 1 1 auto;
`;

interface IHeaderRootProps {
  width: number;
  height: number;
}

//Height for IE11
const HeaderRoot = styled.div`
  height: ${(props: IHeaderRootProps) => (props.height)}px;
  width: ${(props: IHeaderRootProps) => (props.width)}px;
  flex: 0 0 ${VIEWPORT_TITLE_HEIGHT}px;
  height: ${VIEWPORT_TITLE_HEIGHT}px;
  display: flex;
  flex-direction: column;
  font-weight: bold;
  text-transform: uppercase;
  border-bottom: solid 1px ${BORDER_COLOR};
  position: sticky;
  top: 0px;
  z-index: 20;
`;

interface IVerticalSeparatorProps {
  height: number;
}

export const VerticalSeparator = styled.div`
  height: ${(props: IVerticalSeparatorProps) => (props.height)}px;
  position: absolute;
  float: right;
  border-right: 1px solid ${VERTICAL_SEPARATOR_COLOR};
  z-index: 1;
`;

interface IHeaderProps extends WithTranslation {
  displaySettings: DisplaySettings;
  startDate: Date;
  endDate: Date;
  hourWidth: number;
  zoomMode: string;

  saveDayClicked: (date: Date) => void
}

class Header extends PureComponent<IHeaderProps> {

  index: number;

  useTodayColour = (headerUnit: string, rowIndex: number, currentTime: Date) => {
    if (!this.props.displaySettings.showCurrentDay || rowIndex !== 1) {
      return false;
    }
    if (headerUnit === HEADER_UNIT_SUB_MONTH) {
      return isThisMonth(currentTime);
    } else if (headerUnit === HEADER_UNIT_SUB_DAY) {
      return isToday(currentTime);
    } else if (headerUnit === HEADER_UNIT_SUB_HOUR) {
      return isThisHour(currentTime);
    }
  };

  onDateElementClick = (event: MouseEvent, date: Date) => {

    if (event.detail === 2) { // on double click
      if ( this.props.zoomMode === ZOOM_MODE_WEEK || this.props.zoomMode === ZOOM_MODE_MONTH ) {
        this.props.saveDayClicked(date)
      }
    }
  }

  renderHeaderRow = (headerUnit: string, rowIndex: number): ReactElement => {
    const rowElements: ReactElement[] = [];
    let currentTime = this.props.startDate;
    let nextTime: Date;
    const { t } = this.props;

    const headerUnitFormat = t(headerUnitInfo[headerUnit].format);

    const isClickable = (this.props.zoomMode === ZOOM_MODE_DAY || this.props.zoomMode === ZOOM_MODE_YEAR) ? false : true;

    while (currentTime < this.props.endDate) {
      nextTime = getNextRoundedDate(headerUnit, currentTime, this.props.displaySettings.startOfWeekDay);

      if (nextTime > this.props.endDate) {
        nextTime = this.props.endDate;
      }
      const dayIsHoliday   = this.props.displaySettings.showHolidays ? isHoliday(currentTime) : false;
      const elemWidth      = differenceInHours(nextTime, currentTime) * this.props.hourWidth;
      const useTodayColour = this.useTodayColour(headerUnit, rowIndex, currentTime);
      const isWeekendDay   = (isWeekend(currentTime) || dayIsHoliday) && [HEADER_UNIT_SUB_DAY, HEADER_UNIT_SUB_HOUR].includes(headerUnit);
      const date = currentTime;

      rowElements.push(
        <DateElement 
          onClick={rowIndex === 1 ? (event) => this.onDateElementClick(event, date) : null} 
          key={this.index++} 
          width={elemWidth} 
          rowIndex={rowIndex} 
          isWeekend={isWeekendDay} 
          useTodayColour={useTodayColour}
        >
          {rowIndex === 0 && currentTime !== this.props.startDate ? <VerticalSeparator height={VIEWPORT_TITLE_HEIGHT} /> : null}
          <DateElementInner 
            key={this.index++} 
            justify={headerUnitInfo[headerUnit].justify}
            isClickable={rowIndex === 1 ? isClickable : false}
          >
            {format(currentTime, headerUnitFormat)}
          </DateElementInner>
        </DateElement>
      );
      currentTime = nextTime;
    }
    return <>{rowElements}</>;
  };

  renderHeader = (): ReactElement => {
    const headerRows = [];
    const headerUnits: string[] = zoomModeInfo[this.props.zoomMode].headerUnits;
    for (let i = 0; i < headerUnits.length; i++) {
      headerRows.push(<HeaderRow key={this.index++}>{this.renderHeaderRow(headerUnits[i], i)}</HeaderRow>);
    }
    return <>{headerRows}</>;
  };

  render(): ReactElement {
    this.index = 0;
    const chartTotalWidth = getChartTotalWidth(this.props.hourWidth, this.props.startDate, this.props.endDate);
    return (
      <HeaderRoot height={VIEWPORT_TITLE_HEIGHT} width={chartTotalWidth}>
        {this.renderHeader()}
      </HeaderRoot>
    );
  }
}

const mapState = (state: iRootState) => ({
  displaySettings: state.UI.displaySettings,
});

export default withTranslation() (connect(
  mapState, 
  (dispatch: Dispatch) => ({
    saveDayClicked: (date: Date) => {
      dispatch.UI.setDayClicked( date );
      dispatch.UI.setZoomMode(ZOOM_MODE_DAY);
    },
  })
)(Header));
